<template>
    <div v-can="'FpAgen1'" id="full-calendar" class="refactory">
      <Header
        :fullCalendar="fullCalendar"
        :currentView="currentView"
        :currentMonth="currentMonth"
        :loadAppointments="loadAppointments"
        @changeCurrentView="changeCurrentView"
        @changeDate="changeDate"
        @filterChanged="filterChanged"
        @openAppointment="openAppointment"
        @setOpeningHours="setOpeningHours"
        @setInterval="setCalendarInterval"
      />
  
      <section class="calendar-title">
        <span 
          v-if="currentView === 'timeGridDay'"
          class="day-title"
        >
          {{ this.moment(this.start).format('dddd').toUpperCase() }}
        </span>
      </section>
      <FullCalendar
        ref="fullCalendar"
        class="fc fc-media-screen fc-direction-ltr fc-theme-standard"
        :class="{'week': currentView === 'timeGridWeek'}"
        :options="calendarOptions"
        @hook:mounted="calendarMount"
      />
  
      <SearchAppointmentModal
        @openAppointment="openAppointment"
        :loadAppointments="loadAppointments"
      />
  
      <AppointmentModal
        :appointmentId="appointmentId"
        :professionalId="professionalObject"
        :roomId="roomId"
        :date="date"
        @loadAppointments="loadAppointments"
        @onHidden="onHiddenAppointmentModal"
        @openAppointment="openAppointment"
        :patientProps="patient"
        :setAppointmentId="setAppointmentId"
      />
  
      <SuccessSchedulingModal
        @openAppointment="openAppointment"
      />
  
      <NewPatientModal
        @create-patient="createPatient"
      />
    </div>
  </template>
  <script>
  import { mapActions, mapState } from 'vuex'
  import { BPopover } from 'bootstrap-vue'
  import { getCurrentUser, getCurrentClinic, userHasPermission, getCalendarView } from '@/utils/localStorageManager'
  import moment from 'moment'
  import dayGridPlugin from '@fullcalendar/daygrid'
  import timeGridPlugin from '@fullcalendar/timegrid'
  import interactionPlugin from '@fullcalendar/interaction'
  import FullCalendar from '@fullcalendar/vue'
  import Tooltip from '@/modules/schedule2/components/Tooltip'
  
  export default {
    components: {
      FullCalendar: FullCalendar,
      AppointmentModal: () => import('@/components/AppointmentModal'),
      Header: () => import('@/modules/schedule/layouts/Header'),
      NewPatientModal: () => import('@/components/Schedule/NewPatientModal'),
      SearchAppointmentModal: () => import('@/components/Schedule/SearchAppointmentModal'),
      SuccessSchedulingModal: () => import('@/components/General/SuccessSchedulingModal.vue'),
    },
    computed: {
      ...mapState('schedule', ['loadingApointments', 'events', 'selectedSchedules']),
    },
    async mounted() {
      this.checkPeriods();
      await this.reloadEvents();
      await this.filterChanged();
    },
    data() {
      const user = getCurrentUser()
      const currentView = getCalendarView(user.cpf)
      return {
        clinic: getCurrentClinic(),
        currentView,
        fullCalendar: null,
  
        date: null,
        start: moment().startOf('month').startOf('week').format('YYYY-MM-DD'),
        end: moment().endOf('month').endOf('week').format('YYYY-MM-DD'),
        currentMonth: moment().format('MM'),
  
        appointmentId: null,
        professionalIds: [],
        professionalId: null,
        professionalObject: null,
        roomId: null,
        procedureType: null,
        financeiroValue: null,
        priorityLevel: null,
        pendency: null,
        workTimes: [],
        outages: [],
        clinicExceptions: [],
        durationTime: JSON.parse(localStorage.getItem('calendarInterval')) ?? 30,
        // doctorIntervals: [],
  
        periodsForm: [],
        outagesReasons: [],
        outagesReasonsTimeGrid: [],
        allEvents: [],
  
        calendarOptions: {
          height: '100%',
          plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
          initialView: currentView,
          headerToolbar: false,
          allDaySlot: false,
          nowIndicator: true,
          slotMinWidth: 256,
          locale: 'pt-br',
          slotDuration: '00:30:00',
          slotLabelInterval:  JSON.parse(localStorage.getItem('calendarInterval')) ?? 30,
          events: [],
          dayCellContent: this.handleDayCellContent,
          dayCellClassNames : this.handleDayRender,
          slotLabelClassNames: this.handleSlotRender,
          dayHeaderContent: this.handleDayHeaderContent,
          eventContent: this.handleEventContent,
          eventDidMount: this.handleEventDidMount,
          moreLinkContent: this.handleMoreLinkContent,
          eventClick: this.handleEventClick,
          dateClick: this.handleDateClick,
          // allDayClassNames: this.handleAllDay,
          views: {
            dayGridMonth: {
              fixedWeekCount: false,
              dayMaxEventRows: 4
            },
            timeGridDay: {
              type: 'timeGridDay',
              duration: { days: 1 },
            }
          },
          slotLabelFormat: {
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: false,
            meridiem: 'short'
          }
        },
        days: [],
        patient: {},
        openingMinutes: null
      }
    },
    methods: {
      ...mapActions('schedule', ['getAppointments']),
      calendarMount() {
        this.fullCalendar = this.$refs.fullCalendar
      },
      async setOpeningHours(nextDate, weekDay) {
        let startHours = [];
        let endHours = [];
  
        this.openingMinutes = {
          start: this.durationTime,
          end: this.durationTime
        };
  
        if (!weekDay) {
          let startWeek = moment().day(0).format('YYYY-MM-DD');
          let endWeek = moment().day(6).format('YYYY-MM-DD');
  
          if (nextDate) {
            startWeek = nextDate.format('YYYY-MM-DD');
            endWeek = nextDate.endOf('week').format('YYYY-MM-DD');
          }
  
          this.days.forEach(day => {
            this.exceptions.forEach(exception => {
              if (moment(exception.date_start).isBetween(startWeek, endWeek,  undefined, '[)')) {
                startHours.push(exception.start);
                endHours.push(exception.end);
              }
            });
            if (day.status) {
              day.schedule_config_day_intervals.forEach(interval => {
                startHours.push(interval.start_time);
                endHours.push(interval.end_time);
              });
            }  
          });
        }
        else {
          const exceptionDay = this.exceptions.find(exception => {
            return moment(exception.date_start).isBetween(weekDay, exception.date_end,  undefined, '[]')
          })
          if (exceptionDay) {
            startHours.push(exceptionDay.start);
            endHours.push(exceptionDay.end);
          } 
          else {
            const day = this.days.find(d => {
              return d.dayOfWeekInt === moment(weekDay).weekday()
            });
              day.schedule_config_day_intervals.forEach(interval => {
                startHours.push(interval.start_time);
                endHours.push(interval.end_time);
              });
          }
        }
        startHours = startHours.sort()[0]
        endHours = endHours.sort().at(-1)
  
        await this.updateOpeningHours(startHours, endHours, this.openingMinutes);
      },
      async updateOpeningHours(start, end, minutes) {
        if (!start || !end || !minutes) return;
        try {
          const startOfDay = moment(start, 'h:mm:ss').startOf('day')
          const endOfDay = moment(start, 'h:mm:ss').endOf('day')
          let minTime = moment(start, 'h:mm:ss').subtract(minutes.start, 'minutes')
          let maxTime = moment(end, 'h:mm:ss').add(minutes.end, 'minutes')
          minTime = minTime > startOfDay ? minTime : startOfDay
          maxTime = maxTime < endOfDay ? maxTime : endOfDay
  
          const api = await this.$refs.fullCalendar?.getApi()
          if(api) {
            await api.setOption('slotMinTime', minTime.format('LTS'));
            await api.setOption('slotMaxTime', maxTime.format('LTS'));
          }
          
        } catch (error) {
          console.log(error)
        }
      },
      openAppointment(appointmentId) {
        this.appointmentId = appointmentId;
        this.$bvModal.show('appointment-modal')
      },
      async changeDate(calendarRange) {
        if (this.currentView === 'dayGridMonth') {
          calendarRange.startDate.startOf('week');
          calendarRange.endDate.endOf('week');
        }
        this.start = calendarRange.startDate.format('YYYY-MM-DD');
        this.end = calendarRange.endDate.format('YYYY-MM-DD');
        await this.loadAppointments();
      },
      async filterChanged(filters = null) {
        if(filters){
          this.financeiroValue = filters.financeiroValue;
          this.roomId = filters.roomId;
          this.procedureType = filters.procedureType;
          this.pendency = filters.pendency;
          this.priorityLevel = filters.priorityLevel.map(el => el.value);
        }
        try {
          const res = await this.getScheduleConfig(this.clinic.id, this.roomId, this.professionalId);
          res && this.reloadCalendarConfigCells(res.data);
          res && this.loadAppointments();
        } catch(ex) {
          this.$toast.error(ex.message);
          console.log(ex)
        }
      },
      async getScheduleConfig() {

        const res = await this.api.getScheduleConfig(this.clinic.id, this.roomId, this.professionalId);
        const { days, exceptions, outages } = res.data;
  
        this.workTimes = days.map(workTime => {
          workTime.dayOfWeekInt = this.translateDays(workTime.day);
          return workTime;
        });
  
        this.days = days
        this.exceptions = exceptions;
        this.outages = outages;
        this.setOpeningHours();
        return res;
      },
      setCalendarInterval (value) {
        this.durationTime = value ?? 30
        this.setOpeningHours()
        this.calendarOptions.slotDuration = this.durationTime * 60 * 1000
      },
      async reloadCalendarConfigCells(data) {
        const { days, exceptions } = data;
        this.durationTime = JSON.parse(localStorage.getItem('calendarInterval')) ?? 30;
        this.calendarOptions.slotDuration = this.durationTime * 60 * 1000
        this.clinicExceptions = exceptions;
        const api = await this.$refs.fullCalendar.getApi()
        await api.setOption('slotLabelInterval', this.durationTime);
  
        // this.doctorIntervals = days.map(data => {
        //   return {
        //     day: this.translateDays(data.day),
        //     start: data.start,
        //     end: data.end
        //   }
        // })
        this.workTimes = days.map(workTime => {
          workTime.dayOfWeekInt = this.translateDays(workTime.day);
          return workTime;
        });
      },
      async loadAppointments() {
        if (!this.selectedSchedules.length) return;
        const params = {
          start: this.start,
          end: this.end,
          view: this.currentView,
          professionalIds: this.selectedSchedules.map(el => el.id),
          procedureType: this.procedureType,
          financeiroValue: this.financeiroValue,
          pendency: this.pendency,
          priorityLevel: this.priorityLevel,
        };

        this.getAppointments(params)
      },
      getEventStartTime(appointment) {
        return (appointment.clinic?.attendance_type === 'ARRIVAL' && appointment.start_datetime) || appointment.start_datetime
      },
      getEventEndTime(appointment) {
        const diff = moment(appointment.end_datetime).diff(moment(appointment.end_datetime), 'minutes')
        return (appointment.clinic?.attendance_type === 'ARRIVAL' && appointment.end_datetime && moment(appointment.end_datetime).add(diff, 'minutes').format('YYYY-MM-DD HH:mm:ss')) || appointment.end_datetime
      },
      onHiddenAppointmentModal() {
        this.appointmentId = null
        this.date = null
      },
      changeCurrentView(view) {
        this.currentView = view;
      },
      handleEventClick(arg) {
        const popover = document.querySelector('.fc-popover')
        if (popover) popover.remove()
        this.openAppointment(arg.event.extendedProps.appointment.id)
      },
      handleDateClick(arg) {
        if (moment(arg.date).isBefore(moment().startOf('day'))) return;
  
        if(!userHasPermission('FpAgen2')) {
          this.$toast.warning('Você não possui permissão para criar agendamentos')
          return
        }
  
        // this.eventHub.$emit('appointment-modal-date', arg.date);
        this.date = arg.date;
        this.professionalObject = this.selectedSchedules[0]
        this.$bvModal.show('appointment-modal');
      },
      handleDayCellContent(arg){
        const dayNumberHtml = `<p class="day-number ${arg.isToday ? 'is-today' : '' }">${arg.dayNumberText}</p>`;
  
        const professionalAndClinicOutagesOfDay = this.outages.filter(outage => {
          return this.moment(arg.date).isBetween(outage.start, outage.start, undefined, '[]')
            && (!outage.professional_id || this.professionalId === outage.professional_id);
        });
  
        let reasonTooltipContent = '';
        let reasonToolTip = '';
  
        if (professionalAndClinicOutagesOfDay.length > 0){
          reasonTooltipContent = '<div>';
  
          professionalAndClinicOutagesOfDay.forEach((outage) => {
            if(outage.professional_id && this.professionalId === outage.professional_id)
              reasonTooltipContent += `<p>Motivo profissional:</p>${outage.reason.reason}`;
            else
              reasonTooltipContent += `<p>Motivo clínica:</p>${outage.reason.reason}`;
          });
  
          reasonTooltipContent += '</div>';
  
          reasonToolTip = `
            <div class="reason-tooltip-container">
              <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z" stroke="#7F91D2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6.81738 6.75043C6.99371 6.24918 7.34175 5.82651 7.79985 5.55728C8.25795 5.28805 8.79655 5.18963 9.32026 5.27946C9.84397 5.36929 10.319 5.64157 10.6612 6.04807C11.0034 6.45458 11.1907 6.96907 11.1899 7.50043C11.1899 9.00043 8.93988 9.75043 8.93988 9.75043" stroke="#7F91D2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 12.75H9.0075" stroke="#7F91D2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ${reasonTooltipContent}
            </div>`;
        }
  
        const html = `${(this.currentView !== 'timeGridWeek' ? dayNumberHtml : '')}${reasonToolTip}`;
        return { html };
      },
      handleDayRender(arg) {
        if(!this.workTimes.length)
          return  'empty';
  
        const workDay = this.workTimes.find(workTime => {
          return workTime.dayOfWeekInt === arg.date.getDay() && workTime.status && workTime.schedule_config_day_intervals?.length;
        });
  
        const workDayException = this.exceptions.find(exception => {
          return moment(arg.date).isBetween(exception.date_start, exception.date_end, undefined, '[]');
        });
  
        if (!workDay && !workDayException && !this.roomId && !this.professionalId) {
          return 'clinic-unavailable';
        } else if(!workDay && !workDayException && !this.roomId && this.professionalId) {
          return 'professional-unavailable';
        } else if(!workDay && !workDayException && this.roomId) {
          return 'room-unavailable';
        }
  
        const professionalAndClinicOutagesOfDay = this.outages.filter(outage => {
          var start = outage.repeat === 'Anual' ?
            this.moment(this.moment(arg.date).format('YYYY')+"-"+this.moment(outage.start).format('M')+"-"+this.moment(outage.start).format('D')):
            outage.start
  
          var end = outage.repeat === 'Anual' ?
            this.moment(this.moment(arg.date).format('YYYY')+"-"+this.moment(outage.end).format('M')+"-"+this.moment(outage.end).format('D')):
            outage.end
  
          return this.moment(arg.date).isBetween(start, end, undefined, '[]')
            && (!outage.professional_id || this.professionalId === outage.professional_id);
        });
  
        const classesToApplyArray = professionalAndClinicOutagesOfDay.map(outage => {
          if (outage.professional_id !== null && this.professionalId === outage.professional_id) {
            return 'professional-unavailable';
          } else if (outage.room_id !== null && outage.room_id === this.roomId) {
             return 'room-unavailable'
          } else if (!outage.room_id && !outage.professional_id) {
            return 'clinic-unavailable';
          }
        });
  
        return classesToApplyArray.reduce(
          (classesAccumulator, currentClass) => classesAccumulator + ' ' + currentClass,
          ''
        );
      },
      handleSlotRender(arg){
        if(!this.periodsForm.length) return 'null';
  
        let res = 'null'
  
        const time = moment(arg.date).format('HH:mm:ss')
        const startDown = moment(this.periodsForm[0].start, 'HH:mm:ss')
        const endDown = moment(this.periodsForm[0].end, 'HH:mm:ss')
  
        const startMorning = moment(this.periodsForm[1].start, 'HH:mm:ss')
        const endMorning = moment(this.periodsForm[1].end, 'HH:mm:ss')
  
        const startEvening = moment(this.periodsForm[2].start, 'HH:mm:ss')
        const endEvening = moment(this.periodsForm[2].end, 'HH:mm:ss')
  
        const startNight = moment(this.periodsForm[3].start, 'HH:mm:ss')
        const endNight = moment(this.periodsForm[3].end, 'HH:mm:ss')
  
        if(moment(time, 'HH:mm:ss').isBetween(startDown, endDown, undefined, '[]') && this.periodsForm[0].status === 1){
          res = this.periodsForm[0].color_name
        }
        else if(moment(time, 'HH:mm:ss').isBetween(startMorning, endMorning, undefined, '[]') && this.periodsForm[1].status === 1){
          res = this.periodsForm[1].color_name
        }
        else if(moment(time, 'HH:mm:ss').isBetween(startEvening, endEvening, undefined, '[]') && this.periodsForm[2].status === 1){
          res = this.periodsForm[2].color_name
        }
        else if(moment(time, 'HH:mm:ss').isBetween(startNight, endNight, undefined, '[]') && this.periodsForm[3].status === 1){
          res = this.periodsForm[3].color_name
        }
  
        return res
      },
      handleDayHeaderContent(arg) {
        if (this.currentView === 'dayGridMonth') {
          const dayNames = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo']
          return dayNames[arg.date.getDay()]
        } else if (this.currentView === 'timeGridWeek' || this.currentView === 'timeGridDay') {
          const isPast = arg.isPast ? 'isPast' : ''
          const isToday = arg.isToday ? 'isToday' : ''
  
          const dayHeaderContent = this.currentView === 'timeGridWeek' ?
          `
            <div class="fc-day-header-content">
              <div class="fc-day-header-dow">
              ${moment(arg.date).format('ddd')}
              </div>
              <div class="fc-day-header-day ${isPast} ${isToday}">
                ${this.moment(arg.date).format('D')}
              </div>
            </div>
          `
          :
          '';
  
          return { html: dayHeaderContent }
        }
      },
      handleEventDidMount(arg) {
        if (this.currentView === 'timeGridDay') {
          const event = arg.event._def
          const data = arg.event._def.extendedProps.appointment
          new BPopover({propsData: {
            html: true,
            content: <Tooltip content={event} data={data} hasProcedure={this.hasProcedure}/>,
            placement: 'bottom',
            boundary: 'viewport',
            boundaryPadding: 10,
            offset: 0,
            triggers: 'hover',
            target: arg.el.querySelector('.icon-tooltip')
          }}).$mount()
        }
      },
      handleEventContent(arg) {
        const event = arg.event
        const start = moment(event.start)
        const end = moment(event.end)
        let eventContent = ''
        const isBefore = moment(event.start).isBefore(moment().startOf('day'))
        const eventDuration = end.diff(start, 'minutes')
        const plans = arg.event.extendedProps.plans
        const plansHtml = plans.map(p => `<span class="text-truncate text-plan">${p}</span>`).join(' /')
  
        if (this.currentView === 'dayGridMonth') {
          for(const work of this.workTimes){
            const workTime = this.translateDays(work.day)
            const status = event.extendedProps.appointment.status ? event.extendedProps.appointment.status.toLowerCase() : 'undefined';
            if(event.start.getDay() === workTime){
              eventContent =
                '' +
                `<div class="fc-content ${isBefore && 'fc-outdated'} ${status}" style="padding: 3px 5px 0px 5px;"><div style="overflow: hidden; display: flex; align-items: center;">` +
                `  <div class="fc-time time-${status}">` +
                this.moment(event.start).format('HH:mm') +
                '</div>' +
                `  <div class="fc-title title-${status}">` +
                event.title +
                '</div>' + '</div>' +
                '<div class="fc-icons">' +
  
                '<div class="attendance-types">' +
                (this.hasProcedure(
                  event.extendedProps.appointment,
                  'PROCEDURE'
                )
                  ? `
                  <div class="fc-icon icon-${status}">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.0909 6.00003C9.2126 6.00003 8.49997 6.71179 8.49997 7.59078V17C8.49997 17.5523 8.94769 18 9.49997 18C10.0523 18 10.5 17.5517 10.5 16.9994V13.5H13.25C15.321 13.5 17 11.8211 17 9.75003C17 7.67896 15.321 6.00003 13.25 6.00003H10.0909ZM13.25 11.5H10.5V8.00003H13.25C14.2165 8.00003 15 8.78353 15 9.75003C15 10.7165 14.2165 11.5 13.25 11.5Z"/>
                    </svg>
                  </div>
                `
                  : '') +
                  (event.extendedProps.appointment.pending_financial === 0
                  ?
                    `<div class="scheduling-status-icon financial">
                      <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6.70414 11.8399C9.92672 11.8399 12.5391 9.2275 12.5391 6.00492C12.5391 2.78234 9.92672 0.169922 6.70414 0.169922C3.48156 0.169922 0.869141 2.78234 0.869141 6.00492C0.869141 9.2275 3.48156 11.8399 6.70414 11.8399ZM6.7045 1.81104C7.04765 1.81104 7.32582 2.08921 7.32582 2.43235V3.00131C7.63832 2.99887 7.94642 3.07728 8.22006 3.22931C8.50736 3.38891 8.74584 3.62364 8.90998 3.90837C9.08135 4.20566 8.97929 4.58558 8.682 4.75696C8.38472 4.92834 8.00479 4.82627 7.83341 4.52899C7.78183 4.4395 7.70688 4.36573 7.61658 4.31557C7.52629 4.2654 7.42406 4.24074 7.32083 4.24422C7.31386 4.24445 7.30689 4.24457 7.29992 4.24457H6.10906C5.958 4.24457 5.81314 4.30457 5.70633 4.41138C5.59952 4.51819 5.53951 4.66306 5.53951 4.81411C5.53951 4.96516 5.59952 5.11003 5.70633 5.21684C5.81314 5.32365 5.958 5.38365 6.10906 5.38365H7.29992C7.78054 5.38365 8.24148 5.57458 8.58132 5.91443C8.92117 6.25428 9.1121 6.71522 9.1121 7.19584C9.1121 7.67646 8.92117 8.13739 8.58132 8.47724C8.24759 8.81098 7.79708 9.0011 7.32582 9.00783V9.57753C7.32582 9.92067 7.04765 10.1988 6.7045 10.1988C6.36136 10.1988 6.08318 9.92067 6.08318 9.57753V9.00864C5.77068 9.01109 5.46256 8.93267 5.18891 8.78064C4.90162 8.62103 4.66314 8.3863 4.499 8.10158C4.32762 7.80429 4.42969 7.42436 4.72697 7.25299C5.02426 7.08161 5.40418 7.18367 5.57556 7.48096C5.62715 7.57045 5.7021 7.64422 5.79239 7.69438C5.88268 7.74454 5.98492 7.76921 6.08815 7.76573C6.09512 7.7655 6.10209 7.76538 6.10906 7.76538H6.69809L6.7045 7.76535L6.71092 7.76538H7.29992C7.45097 7.76538 7.59584 7.70537 7.70265 7.59856C7.80946 7.49175 7.86946 7.34689 7.86946 7.19584C7.86946 7.04478 7.80946 6.89992 7.70265 6.79311C7.59584 6.6863 7.45097 6.62629 7.29992 6.62629H6.10906C5.62844 6.62629 5.1675 6.43537 4.82765 6.09552C4.4878 5.75567 4.29688 5.29473 4.29688 4.81411C4.29688 4.33349 4.4878 3.87256 4.82765 3.53271C5.1614 3.19896 5.61192 3.00883 6.08318 3.00211V2.43235C6.08318 2.08921 6.36136 1.81104 6.7045 1.81104Z" fill="#0B30B2"/>
                      </svg>
                      </div>`
                    : '') +
                (this.hasProcedure(
                  event.extendedProps.appointment,
                  'EXAM'
                )
                  ? `
                  <div class="fc-icon icon-${status} exam">
                    <svg viewBox="0 0 20 20" fill="black" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM7.37812 16C6.61701 16 6 15.3722 6 14.5979V5.40214C6 4.62776 6.61701 4 7.37812 4H12.9503C13.53 4 14 4.47814 14 5.06797C14 5.65779 13.53 6.13593 12.9503 6.13593H9.35573C8.84832 6.13593 8.43698 6.55444 8.43698 7.07069V7.9073C8.43698 8.42356 8.84832 8.84207 9.35573 8.84207H12.5966C13.1763 8.84207 13.6463 9.32021 13.6463 9.91003C13.6463 10.4999 13.1763 10.978 12.5966 10.978H9.35573C8.84832 10.978 8.43698 11.3965 8.43698 11.9128V12.9293C8.43698 13.4456 8.84832 13.8641 9.35573 13.8641H12.9503C13.53 13.8641 14 14.3422 14 14.932C14 15.5219 13.53 16 12.9503 16H7.37812ZM6.22969 14.5979C6.22969 15.2432 6.74386 15.7663 7.37812 15.7663H12.9503C13.032 15.7663 13.1108 15.7542 13.1852 15.7316C13.1108 15.7542 13.032 15.7663 12.9504 15.7663H7.37817C6.74391 15.7663 6.22974 15.2432 6.22974 14.5978V5.40212C6.22974 4.91688 6.52045 4.50073 6.93435 4.32412C6.52043 4.50072 6.22969 4.91688 6.22969 5.40214V14.5979ZM9.35573 10.7443C9.23402 10.7443 9.11674 10.7636 9.00666 10.7993C9.11676 10.7636 9.23405 10.7443 9.35578 10.7443H12.5967C12.6745 10.7443 12.7498 10.7332 12.8212 10.7126C12.7498 10.7333 12.6745 10.7443 12.5966 10.7443H9.35573ZM9.35573 5.90224C9.23588 5.90224 9.12032 5.92092 9.01171 5.95557C9.12034 5.92091 9.23591 5.90222 9.35578 5.90222H12.9504C13.026 5.90222 13.0993 5.8918 13.1688 5.87229C13.0993 5.89181 13.026 5.90224 12.9503 5.90224H9.35573Z"/>
                    </svg>
                  </div>
                `
                  : '') +
                (this.hasProcedure(
                  event.extendedProps.appointment,
                  'TELEMEDICINE'
                )
                  ? `
                  <div class="fc-icon icon-${status}">
                    <svg viewBox="0 0 24 24" fill="#0C1D59" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 7L16 12L23 17V7Z"/>
                      <path d="M14 5H3C1.89543 5 1 5.89543 1 7V17C1 18.1046 1.89543 19 3 19H14C15.1046 19 16 18.1046 16 17V7C16 5.89543 15.1046 5 14 5Z"/>
                    </svg>
                  </div>
                `
                  : '') +
                (this.hasProcedure(
                  event.extendedProps.appointment,
                  'APPOINTMENT'
                )
                  ? `
                  <div class="fc-icon icon-${status}">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.25 12C8.25 9.92893 9.92893 8.25 12 8.25H13.25C13.9404 8.25 14.5 7.69036 14.5 7C14.5 6.30964 13.9404 5.75 13.25 5.75H12C8.54822 5.75 5.75 8.54822 5.75 12C5.75 15.4518 8.54822 18.25 12 18.25H13.25C13.9404 18.25 14.5 17.6904 14.5 17C14.5 16.3096 13.9404 15.75 13.25 15.75H12C9.92893 15.75 8.25 14.0711 8.25 12Z"/>
                    </svg>
                  </div>
                `
                  : '') +
                (this.hasProcedure(
                  event.extendedProps.appointment,
                  'RETURN'
                )
                  ? `
                  <div class="fc-icon icon-${status}">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM9.59091 6C8.71263 6 8 6.71176 8 7.59075V17C8 17.5523 8.44772 18 9 18C9.55228 18 10 17.5517 10 16.9994V13.5H12.75C14.8211 13.5 16.5 11.8211 16.5 9.75C16.5 7.67893 14.8211 6 12.75 6H9.59091ZM12.75 11.5H10V8H12.75C13.7165 8 14.5 8.7835 14.5 9.75C14.5 10.7165 13.7165 11.5 12.75 11.5Z"/>
                        <path d="M11.5 12.5H12.5585C13.4193 12.5 14.1836 13.0509 14.4558 13.8675L15.5 17" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                `
                  : '') +
  
                  (this.hasProcedure(
                    event.extendedProps.appointment,
                    'SURGICAL'
                  )
                    ? `
                    <div class="fc-icon icon-${status}">
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.55185 10.9093C5.55185 13.9217 7.99393 16.3638 11.0064 16.3638C14.0189 16.3638 16.4609 13.9217 16.4609 10.9093C16.4609 7.91784 14.0528 5.48886 11.0694 5.45508C13.1921 5.48206 14.9024 6.86702 14.9024 8.57156C14.9024 10.293 13.158 11.6884 11.0063 11.6884C8.85452 11.6884 7.11018 10.293 7.11018 8.57156C7.11018 6.8675 8.81954 5.48284 10.9414 5.4551C7.95886 5.48995 5.55185 7.91851 5.55185 10.9093Z" />
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M8.45597 9.09144C8.45597 9.60178 9.15608 10.7278 11.0014 10.7278C12.8468 10.7278 13.5469 9.60178 13.5469 9.09144C13.5469 8.5811 12.8468 7.45508 11.0014 7.45508C9.15608 7.45508 8.45597 8.5811 8.45597 9.09144ZM6.45597 9.09144C6.45597 11.0997 8.49104 12.7278 11.0014 12.7278C13.5118 12.7278 15.5469 11.0998 15.5469 9.09144C15.5469 7.08313 13.5118 5.45508 11.0014 5.45508C8.49104 5.45508 6.45597 7.08313 6.45597 9.09144Z" />
                        <path d="M16.7244 2.61743C17.0794 2.26241 17.0794 1.68681 16.7244 1.33178C16.3693 0.976763 15.7937 0.976763 15.4387 1.33178L14.1531 2.61743C13.798 2.97245 13.798 3.54806 14.1531 3.90308C14.5081 4.2581 15.0837 4.2581 15.4387 3.90308L16.7244 2.61743Z" />
                        <path d="M11.9141 0.909091C11.9141 0.407013 11.507 0 11.005 0C10.5029 0 10.0959 0.407014 10.0959 0.909091V8.18182C10.0959 8.6839 10.5029 9.09091 11.005 9.09091C11.507 9.09091 11.9141 8.6839 11.9141 8.18182V0.909091Z" />
                        <path d="M6.91548 1.27558C6.66444 0.840766 6.10845 0.691788 5.67364 0.942827C5.23883 1.19387 5.08985 1.74986 5.34089 2.18467L6.24998 3.75926C6.50102 4.19407 7.05701 4.34305 7.49182 4.09201C7.92663 3.84097 8.07561 3.28498 7.82457 2.85017L6.91548 1.27558Z" />
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M2.14219 12.7278C2.23741 12.8909 2.35946 13.0922 2.50765 13.3216C2.92464 13.9673 3.54203 14.8257 4.34317 15.6802C5.9637 17.4088 8.21769 19.0005 11 19.0005C13.7823 19.0005 16.0363 17.4088 17.6568 15.6802C18.458 14.8257 19.0754 13.9673 19.4923 13.3216C19.6405 13.0922 19.7626 12.8909 19.8578 12.7278C19.7626 12.5647 19.6405 12.3634 19.4923 12.134C19.0754 11.4883 18.458 10.6299 17.6568 9.77538C16.0363 8.04682 13.7823 6.45508 11 6.45508C8.21769 6.45508 5.9637 8.04682 4.34317 9.77538C3.54203 10.6299 2.92464 11.4883 2.50765 12.134C2.35946 12.3634 2.23741 12.5647 2.14219 12.7278ZM21 12.7278C21.8944 12.2806 21.8943 12.2803 21.8941 12.2799L21.8925 12.2768L21.8893 12.2704L21.8787 12.2497C21.8699 12.2325 21.8575 12.2086 21.8415 12.1784C21.8095 12.118 21.7633 12.0323 21.7029 11.925C21.5823 11.7106 21.4049 11.4089 21.1724 11.0489C20.7087 10.3309 20.0193 9.37113 19.1159 8.4075C17.3273 6.4997 14.5813 4.45508 11 4.45508C7.41867 4.45508 4.67266 6.4997 2.8841 8.4075C1.9807 9.37113 1.29127 10.3309 0.827574 11.0489C0.595106 11.4089 0.417738 11.7106 0.297103 11.925C0.236749 12.0323 0.190482 12.118 0.158514 12.1784C0.142527 12.2086 0.130105 12.2325 0.121275 12.2497L0.110724 12.2704L0.107472 12.2768L0.106354 12.279C0.106173 12.2794 0.105573 12.2806 1 12.7278L0.105573 12.2806C-0.0351909 12.5621 -0.0351909 12.8935 0.105573 13.175L1 12.7278C0.105573 13.175 0.105392 13.1747 0.105573 13.175L0.106354 13.1766L0.107472 13.1788L0.110724 13.1852L0.121275 13.2059C0.130105 13.2231 0.142527 13.247 0.158514 13.2772C0.190482 13.3376 0.236749 13.4233 0.297103 13.5306C0.417738 13.745 0.595106 14.0467 0.827574 14.4067C1.29127 15.1247 1.9807 16.0845 2.8841 17.0481C4.67266 18.9559 7.41867 21.0005 11 21.0005C14.5813 21.0005 17.3273 18.9559 19.1159 17.0481C20.0193 16.0845 20.7087 15.1247 21.1724 14.4067C21.4049 14.0467 21.5823 13.745 21.7029 13.5306C21.7633 13.4233 21.8095 13.3376 21.8415 13.2772C21.8575 13.247 21.8699 13.2231 21.8787 13.2059L21.8893 13.1852L21.8925 13.1788L21.8936 13.1766C21.8938 13.1762 21.8944 13.175 21 12.7278ZM21 12.7278L21.8944 13.175C22.0352 12.8935 22.0348 12.5614 21.8941 12.2799L21 12.7278Z" />
                      </svg>
                    </div>
                  `
                    : '') +
                '</div>' +
  
                '<div class="attendance-status">' +
                (event.extendedProps.appointment.status === 'EXAMINATING'
                ?
                  `<div class="scheduling-status-icon examinating-icon">
                    <svg viewBox="0 0 24 24" fill="#0C1D59" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M2 13H8.06189C8.55399 16.9463 11.9204 20 16 20C20.4183 20 24 16.4183 24 12C24 7.58172 20.4183 4 16 4C11.9204 4 8.55399 7.05369 8.06189 11H2C2 10.4477 1.55228 10 1 10C0.447715 10 0 10.4477 0 11V12V13C0 13.5523 0.447715 14 1 14C1.55228 14 2 13.5523 2 13ZM11.1696 15.4502C10.6471 14.7448 10.2613 13.9024 10.0932 13H11C11.5523 13 12 12.5523 12 12C12 11.4477 11.5523 11 11 11H10.0932C10.2614 10.0976 10.6471 9.25526 11.1696 8.54984C11.8971 7.5678 12.7813 6.99122 13.4851 6.81529C14.0884 6.66446 14.5237 6.78701 14.9099 7.24405C15.4071 7.83238 16 9.2091 16 12C16 14.791 15.4071 16.1677 14.9099 16.756C14.5237 17.2131 14.0884 17.3356 13.4851 17.1848C12.7813 17.0088 11.8971 16.4323 11.1696 15.4502ZM16.4928 6.01994C17.3995 7.13959 18 9.05937 18 12C18 14.9406 17.3995 16.8604 16.4928 17.98C19.576 17.7294 22 15.1478 22 12C22 8.85221 19.576 6.27057 16.4928 6.01994Z"/>
                    </svg>
                  </div>`
                    : '') +
                (event.extendedProps.appointment.status === 'DILATING'
                ?
                  `<div class="scheduling-status-icon dilating-icon">
                    <svg viewBox="0 0 24 24" fill="#0C1D59" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12 4C5 4 1 12 1 12C1 12 5 20 12 20C19 20 23 12 23 12C23 12 19 4 12 4ZM10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12ZM12 8.00001C9.79086 8.00001 8 9.79087 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79087 14.2091 8.00001 12 8.00001Z"/>
                    </svg>
                  </div>`
                    : '') +
                (event.extendedProps.appointment.type === 'Encaixe'
                ?
                  `<div class="scheduling-status-icon scheduling_${status}_icon">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="#525C7A" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6ZM6 2.15789C3.87806 2.15789 2.15789 3.87806 2.15789 6C2.15789 8.12194 3.87806 9.8421 6 9.8421C8.12194 9.8421 9.8421 8.12194 9.8421 6C9.8421 3.87806 8.12194 2.15789 6 2.15789ZM1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6ZM6.26663 3.23596C6.26663 2.92173 6.02038 2.66699 5.71663 2.66699C5.41287 2.66699 5.16663 2.92173 5.16663 3.23596V5.64975C5.16663 5.80065 5.22457 5.94537 5.32772 6.05207L6.72772 7.50035C6.94251 7.72254 7.29075 7.72254 7.50553 7.50035C7.72032 7.27815 7.72032 6.9179 7.50553 6.69571L6.26663 5.41408V3.23596Z"/>
                    </svg>
                  </div>`
                    : '') +
                (event.extendedProps.appointment.type === 'Reagendamento'
                ?
                  `<div class="scheduling-status-icon scheduling_${status}_icon">
                    <svg width="12" height="12" viewBox="0 0 13 16" fill="#525C7A" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.79336 1.63232C9.79336 1.21811 9.45757 0.882324 9.04336 0.882324C8.62914 0.882324 8.29336 1.21811 8.29336 1.63232V2.15573H4.69378V1.63232C4.69378 1.21811 4.35799 0.882324 3.94378 0.882324C3.52957 0.882324 3.19378 1.21811 3.19378 1.63232V2.15573H2.02596C0.908376 2.15573 0 3.06039 0 4.1793V6.72618V13.0943C0 14.2132 0.908376 15.1179 2.02596 15.1179H10.9577C12.0752 15.1179 12.9836 14.2132 12.9836 13.0943V6.72618V4.1793C12.9836 3.06039 12.0752 2.15573 10.9577 2.15573H9.79336V1.63232ZM11.4836 5.97618V4.1793C11.4836 3.89146 11.2495 3.65573 10.9577 3.65573H9.79336V4.17947C9.79336 4.59368 9.45757 4.92947 9.04336 4.92947C8.62914 4.92947 8.29336 4.59368 8.29336 4.17947V3.65573H4.69378V4.17947C4.69378 4.59368 4.35799 4.92947 3.94378 4.92947C3.52957 4.92947 3.19378 4.59368 3.19378 4.17947V3.65573H2.02596C1.73415 3.65573 1.5 3.89146 1.5 4.1793V5.97618H11.4836ZM1.5 7.47618H11.4836V13.0943C11.4836 13.3821 11.2495 13.6179 10.9577 13.6179H2.02596C1.73415 13.6179 1.5 13.3821 1.5 13.0943V7.47618Z"/>
                    <path d="M10.7499 2.54199H2.24352C1.57238 2.54199 1.02832 2.75921 1.02832 3.02716V6.42335C1.02832 6.6913 1.57238 6.90852 2.24352 6.90852H10.7499C11.421 6.90852 11.9651 6.6913 11.9651 6.42335V3.02716C11.9651 2.75921 11.421 2.54199 10.7499 2.54199Z"/>
                    </svg>
                  </div>`
                    : '') +
                '</div>' +
                '</div>' +
                '</div>' +
                '</div>' + '<br>'
  
                break;
            } else {
              if (this.clinicExceptions.length > 0){
                for(const clinicException of this.clinicExceptions){
                  const exception = clinicException
                  const compareDate = this.moment(event.start).format('YYYY-MM-DD');
                  const startDate   = this.moment(exception.date_start).format('YYYY-MM-DD');
                  const endDate     = this.moment(exception.date_end).format('YYYY-MM-DD');
  
                  if (this.moment(compareDate).isBetween(startDate, endDate, undefined, '[]')){
                    eventContent =
                      '' +
                      `<div class="fc-content ${isBefore && 'fc-outdated'} ${event.extendedProps.appointment.status.toLowerCase()}" style="padding: 3px 5px 0px 5px;"><div  style="padding: 3px 5px 0px 5px;display: flex;">` +
                      `  <div class="fc-time time-${event.extendedProps.appointment.status.toLowerCase()}">` +
                      this.moment(event.start).format('HH:mm') +
                      '</div>' +
                      `  <div class="fc-title title-${event.extendedProps.appointment.status.toLowerCase()}">` +
                      event.title +
                      '</div>' + '</div>' +
                      '  <div class="fc-icons">' +
                      (this.hasProcedure(
                        event.extendedProps.appointment,
                        'PROCEDURE'
                      )
                        ? `
                        <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.0909 6.00003C9.2126 6.00003 8.49997 6.71179 8.49997 7.59078V17C8.49997 17.5523 8.94769 18 9.49997 18C10.0523 18 10.5 17.5517 10.5 16.9994V13.5H13.25C15.321 13.5 17 11.8211 17 9.75003C17 7.67896 15.321 6.00003 13.25 6.00003H10.0909ZM13.25 11.5H10.5V8.00003H13.25C14.2165 8.00003 15 8.78353 15 9.75003C15 10.7165 14.2165 11.5 13.25 11.5Z"/>
                          </svg>
                        </div>
                      `
                        : '') +
                      (event.extendedProps.appointment.pending_financial === 0
                      ?
                        `<div class="scheduling-status-icon financial">
                          <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M6.70414 11.8399C9.92672 11.8399 12.5391 9.2275 12.5391 6.00492C12.5391 2.78234 9.92672 0.169922 6.70414 0.169922C3.48156 0.169922 0.869141 2.78234 0.869141 6.00492C0.869141 9.2275 3.48156 11.8399 6.70414 11.8399ZM6.7045 1.81104C7.04765 1.81104 7.32582 2.08921 7.32582 2.43235V3.00131C7.63832 2.99887 7.94642 3.07728 8.22006 3.22931C8.50736 3.38891 8.74584 3.62364 8.90998 3.90837C9.08135 4.20566 8.97929 4.58558 8.682 4.75696C8.38472 4.92834 8.00479 4.82627 7.83341 4.52899C7.78183 4.4395 7.70688 4.36573 7.61658 4.31557C7.52629 4.2654 7.42406 4.24074 7.32083 4.24422C7.31386 4.24445 7.30689 4.24457 7.29992 4.24457H6.10906C5.958 4.24457 5.81314 4.30457 5.70633 4.41138C5.59952 4.51819 5.53951 4.66306 5.53951 4.81411C5.53951 4.96516 5.59952 5.11003 5.70633 5.21684C5.81314 5.32365 5.958 5.38365 6.10906 5.38365H7.29992C7.78054 5.38365 8.24148 5.57458 8.58132 5.91443C8.92117 6.25428 9.1121 6.71522 9.1121 7.19584C9.1121 7.67646 8.92117 8.13739 8.58132 8.47724C8.24759 8.81098 7.79708 9.0011 7.32582 9.00783V9.57753C7.32582 9.92067 7.04765 10.1988 6.7045 10.1988C6.36136 10.1988 6.08318 9.92067 6.08318 9.57753V9.00864C5.77068 9.01109 5.46256 8.93267 5.18891 8.78064C4.90162 8.62103 4.66314 8.3863 4.499 8.10158C4.32762 7.80429 4.42969 7.42436 4.72697 7.25299C5.02426 7.08161 5.40418 7.18367 5.57556 7.48096C5.62715 7.57045 5.7021 7.64422 5.79239 7.69438C5.88268 7.74454 5.98492 7.76921 6.08815 7.76573C6.09512 7.7655 6.10209 7.76538 6.10906 7.76538H6.69809L6.7045 7.76535L6.71092 7.76538H7.29992C7.45097 7.76538 7.59584 7.70537 7.70265 7.59856C7.80946 7.49175 7.86946 7.34689 7.86946 7.19584C7.86946 7.04478 7.80946 6.89992 7.70265 6.79311C7.59584 6.6863 7.45097 6.62629 7.29992 6.62629H6.10906C5.62844 6.62629 5.1675 6.43537 4.82765 6.09552C4.4878 5.75567 4.29688 5.29473 4.29688 4.81411C4.29688 4.33349 4.4878 3.87256 4.82765 3.53271C5.1614 3.19896 5.61192 3.00883 6.08318 3.00211V2.43235C6.08318 2.08921 6.36136 1.81104 6.7045 1.81104Z" fill="#0B30B2"/>
                          </svg>
                          </div>`
                        : '') +
                      (this.hasProcedure(
                        event.extendedProps.appointment,
                        'EXAM'
                      )
                        ? `
                        <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                          <svg viewBox="0 0 20 20" fill="black" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM7.37812 16C6.61701 16 6 15.3722 6 14.5979V5.40214C6 4.62776 6.61701 4 7.37812 4H12.9503C13.53 4 14 4.47814 14 5.06797C14 5.65779 13.53 6.13593 12.9503 6.13593H9.35573C8.84832 6.13593 8.43698 6.55444 8.43698 7.07069V7.9073C8.43698 8.42356 8.84832 8.84207 9.35573 8.84207H12.5966C13.1763 8.84207 13.6463 9.32021 13.6463 9.91003C13.6463 10.4999 13.1763 10.978 12.5966 10.978H9.35573C8.84832 10.978 8.43698 11.3965 8.43698 11.9128V12.9293C8.43698 13.4456 8.84832 13.8641 9.35573 13.8641H12.9503C13.53 13.8641 14 14.3422 14 14.932C14 15.5219 13.53 16 12.9503 16H7.37812ZM6.22969 14.5979C6.22969 15.2432 6.74386 15.7663 7.37812 15.7663H12.9503C13.032 15.7663 13.1108 15.7542 13.1852 15.7316C13.1108 15.7542 13.032 15.7663 12.9504 15.7663H7.37817C6.74391 15.7663 6.22974 15.2432 6.22974 14.5978V5.40212C6.22974 4.91688 6.52045 4.50073 6.93435 4.32412C6.52043 4.50072 6.22969 4.91688 6.22969 5.40214V14.5979ZM9.35573 10.7443C9.23402 10.7443 9.11674 10.7636 9.00666 10.7993C9.11676 10.7636 9.23405 10.7443 9.35578 10.7443H12.5967C12.6745 10.7443 12.7498 10.7332 12.8212 10.7126C12.7498 10.7333 12.6745 10.7443 12.5966 10.7443H9.35573ZM9.35573 5.90224C9.23588 5.90224 9.12032 5.92092 9.01171 5.95557C9.12034 5.92091 9.23591 5.90222 9.35578 5.90222H12.9504C13.026 5.90222 13.0993 5.8918 13.1688 5.87229C13.0993 5.89181 13.026 5.90224 12.9503 5.90224H9.35573Z"/>
                          </svg>
                        </div>
                      `
                        : '') +
                      (this.hasProcedure(
                        event.extendedProps.appointment,
                        'TELEMEDICINE'
                      )
                        ? `
                        <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                          <svg viewBox="0 0 24 24" fill="#0C1D59" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23 7L16 12L23 17V7Z"/>
                            <path d="M14 5H3C1.89543 5 1 5.89543 1 7V17C1 18.1046 1.89543 19 3 19H14C15.1046 19 16 18.1046 16 17V7C16 5.89543 15.1046 5 14 5Z"/>
                          </svg>
                        </div>
                      `
                        : '') +
                      (this.hasProcedure(
                        event.extendedProps.appointment,
                        'APPOINTMENT'
                      )
                        ? `
                        <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.25 12C8.25 9.92893 9.92893 8.25 12 8.25H13.25C13.9404 8.25 14.5 7.69036 14.5 7C14.5 6.30964 13.9404 5.75 13.25 5.75H12C8.54822 5.75 5.75 8.54822 5.75 12C5.75 15.4518 8.54822 18.25 12 18.25H13.25C13.9404 18.25 14.5 17.6904 14.5 17C14.5 16.3096 13.9404 15.75 13.25 15.75H12C9.92893 15.75 8.25 14.0711 8.25 12Z"/>
                          </svg>
                        </div>
                      `
                        : '') +
                      (this.hasProcedure(
                        event.extendedProps.appointment,
                        'RETURN'
                      )
                        ? `
                        <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM9.59091 6C8.71263 6 8 6.71176 8 7.59075V17C8 17.5523 8.44772 18 9 18C9.55228 18 10 17.5517 10 16.9994V13.5H12.75C14.8211 13.5 16.5 11.8211 16.5 9.75C16.5 7.67893 14.8211 6 12.75 6H9.59091ZM12.75 11.5H10V8H12.75C13.7165 8 14.5 8.7835 14.5 9.75C14.5 10.7165 13.7165 11.5 12.75 11.5Z"/>
                              <path d="M11.5 12.5H12.5585C13.4193 12.5 14.1836 13.0509 14.4558 13.8675L15.5 17" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </div>
                      `
                        : '') +
  
                        (this.hasProcedure(
                          event.extendedProps.appointment,
                          'SURGICAL'
                        )
                        ? `
                          <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M5.55185 10.9093C5.55185 13.9217 7.99393 16.3638 11.0064 16.3638C14.0189 16.3638 16.4609 13.9217 16.4609 10.9093C16.4609 7.91784 14.0528 5.48886 11.0694 5.45508C13.1921 5.48206 14.9024 6.86702 14.9024 8.57156C14.9024 10.293 13.158 11.6884 11.0063 11.6884C8.85452 11.6884 7.11018 10.293 7.11018 8.57156C7.11018 6.8675 8.81954 5.48284 10.9414 5.4551C7.95886 5.48995 5.55185 7.91851 5.55185 10.9093Z" />
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.45597 9.09144C8.45597 9.60178 9.15608 10.7278 11.0014 10.7278C12.8468 10.7278 13.5469 9.60178 13.5469 9.09144C13.5469 8.5811 12.8468 7.45508 11.0014 7.45508C9.15608 7.45508 8.45597 8.5811 8.45597 9.09144ZM6.45597 9.09144C6.45597 11.0997 8.49104 12.7278 11.0014 12.7278C13.5118 12.7278 15.5469 11.0998 15.5469 9.09144C15.5469 7.08313 13.5118 5.45508 11.0014 5.45508C8.49104 5.45508 6.45597 7.08313 6.45597 9.09144Z" />
                              <path d="M16.7244 2.61743C17.0794 2.26241 17.0794 1.68681 16.7244 1.33178C16.3693 0.976763 15.7937 0.976763 15.4387 1.33178L14.1531 2.61743C13.798 2.97245 13.798 3.54806 14.1531 3.90308C14.5081 4.2581 15.0837 4.2581 15.4387 3.90308L16.7244 2.61743Z" />
                              <path d="M11.9141 0.909091C11.9141 0.407013 11.507 0 11.005 0C10.5029 0 10.0959 0.407014 10.0959 0.909091V8.18182C10.0959 8.6839 10.5029 9.09091 11.005 9.09091C11.507 9.09091 11.9141 8.6839 11.9141 8.18182V0.909091Z" />
                              <path d="M6.91548 1.27558C6.66444 0.840766 6.10845 0.691788 5.67364 0.942827C5.23883 1.19387 5.08985 1.74986 5.34089 2.18467L6.24998 3.75926C6.50102 4.19407 7.05701 4.34305 7.49182 4.09201C7.92663 3.84097 8.07561 3.28498 7.82457 2.85017L6.91548 1.27558Z" />
                              <path fill-rule="evenodd" clip-rule="evenodd" d="M2.14219 12.7278C2.23741 12.8909 2.35946 13.0922 2.50765 13.3216C2.92464 13.9673 3.54203 14.8257 4.34317 15.6802C5.9637 17.4088 8.21769 19.0005 11 19.0005C13.7823 19.0005 16.0363 17.4088 17.6568 15.6802C18.458 14.8257 19.0754 13.9673 19.4923 13.3216C19.6405 13.0922 19.7626 12.8909 19.8578 12.7278C19.7626 12.5647 19.6405 12.3634 19.4923 12.134C19.0754 11.4883 18.458 10.6299 17.6568 9.77538C16.0363 8.04682 13.7823 6.45508 11 6.45508C8.21769 6.45508 5.9637 8.04682 4.34317 9.77538C3.54203 10.6299 2.92464 11.4883 2.50765 12.134C2.35946 12.3634 2.23741 12.5647 2.14219 12.7278ZM21 12.7278C21.8944 12.2806 21.8943 12.2803 21.8941 12.2799L21.8925 12.2768L21.8893 12.2704L21.8787 12.2497C21.8699 12.2325 21.8575 12.2086 21.8415 12.1784C21.8095 12.118 21.7633 12.0323 21.7029 11.925C21.5823 11.7106 21.4049 11.4089 21.1724 11.0489C20.7087 10.3309 20.0193 9.37113 19.1159 8.4075C17.3273 6.4997 14.5813 4.45508 11 4.45508C7.41867 4.45508 4.67266 6.4997 2.8841 8.4075C1.9807 9.37113 1.29127 10.3309 0.827574 11.0489C0.595106 11.4089 0.417738 11.7106 0.297103 11.925C0.236749 12.0323 0.190482 12.118 0.158514 12.1784C0.142527 12.2086 0.130105 12.2325 0.121275 12.2497L0.110724 12.2704L0.107472 12.2768L0.106354 12.279C0.106173 12.2794 0.105573 12.2806 1 12.7278L0.105573 12.2806C-0.0351909 12.5621 -0.0351909 12.8935 0.105573 13.175L1 12.7278C0.105573 13.175 0.105392 13.1747 0.105573 13.175L0.106354 13.1766L0.107472 13.1788L0.110724 13.1852L0.121275 13.2059C0.130105 13.2231 0.142527 13.247 0.158514 13.2772C0.190482 13.3376 0.236749 13.4233 0.297103 13.5306C0.417738 13.745 0.595106 14.0467 0.827574 14.4067C1.29127 15.1247 1.9807 16.0845 2.8841 17.0481C4.67266 18.9559 7.41867 21.0005 11 21.0005C14.5813 21.0005 17.3273 18.9559 19.1159 17.0481C20.0193 16.0845 20.7087 15.1247 21.1724 14.4067C21.4049 14.0467 21.5823 13.745 21.7029 13.5306C21.7633 13.4233 21.8095 13.3376 21.8415 13.2772C21.8575 13.247 21.8699 13.2231 21.8787 13.2059L21.8893 13.1852L21.8925 13.1788L21.8936 13.1766C21.8938 13.1762 21.8944 13.175 21 12.7278ZM21 12.7278L21.8944 13.175C22.0352 12.8935 22.0348 12.5614 21.8941 12.2799L21 12.7278Z" />
                            </svg>
                          </div>
                      `
                      : '') +
                      (event.extendedProps.appointment.type === 'Encaixe'
                      ?
                        `<div style ="margin-left: auto" class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6ZM6 2.15789C3.87806 2.15789 2.15789 3.87806 2.15789 6C2.15789 8.12194 3.87806 9.8421 6 9.8421C8.12194 9.8421 9.8421 8.12194 9.8421 6C9.8421 3.87806 8.12194 2.15789 6 2.15789ZM1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6ZM6.26664 3.23584C6.26664 2.9216 6.0204 2.66687 5.71664 2.66687C5.41288 2.66687 5.16664 2.9216 5.16664 3.23584V5.64963C5.16664 5.80053 5.22459 5.94525 5.32773 6.05195L6.72773 7.50022C6.94252 7.72242 7.29076 7.72242 7.50555 7.50022C7.72034 7.27803 7.72034 6.91778 7.50555 6.69559L6.26664 5.41396V3.23584Z"/>
                          </svg>
                        </div>`
                          : '') +
                      '</div>' +
                      '</div>' + '<br>'
  
                      break;
                  } else {
                    eventContent = ''
                  }
                }
              } else {
                eventContent = ''
              }
            }
          }
  
          // this.checkWorkTime(event, this.workTimes)
        } else if (this.currentView === 'timeGridWeek' || this.currentView === 'timeGridDay') {
          eventContent =
            '' +
            `<div class="fc-content fc-day-content event-${event.extendedProps.appointment.status.toLowerCase()} ${
              isBefore &&
              `event-${event.extendedProps.appointment.status.toLowerCase()}-outdated`} 
              ">` +
            '<div>' +
            `<div class="fc-title title-${event.extendedProps.appointment.status.toLowerCase()}">` +
              `<span class="d-inline-block text-truncate ${this.currentView === 'timeGridDay' ? 'day' : ''}">${event.title} |</span>` + plansHtml + (this.currentView === 'timeGridDay'
              ?
              `<div class="fc-icon icon-tooltip icon-${event.extendedProps.appointment.status.toLowerCase()}">
                <svg viewBox="0 0 24 24" fill="none" stroke="#0C1D59" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 10H3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M21 6H3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M21 14H3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17 18H3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>`
              : '') +
            '</div>' +
            `  <div class="fc-time time-${event.extendedProps.appointment.status.toLowerCase()}">` +
            '</div>' +
            (this.currentView !== 'timeGridDay' && this.durationTime <= eventDuration ?
              `<div class="fc-time time-${event.extendedProps.appointment.status.toLowerCase()} fc-hours">` +
                this.moment(event.start).format('HH:mm') +
                ' até ' +
                this.moment(event.end).format('HH:mm') +
              '</div>' : '') +
            '</div>' +
            '<div class="fc-columns">' +
  
            '<div class="attendance-types">' +
              (this.hasProcedure(
                event.extendedProps.appointment,
                'PROCEDURE'
              )
              ? `
              <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.0909 6.00003C9.2126 6.00003 8.49997 6.71179 8.49997 7.59078V17C8.49997 17.5523 8.94769 18 9.49997 18C10.0523 18 10.5 17.5517 10.5 16.9994V13.5H13.25C15.321 13.5 17 11.8211 17 9.75003C17 7.67896 15.321 6.00003 13.25 6.00003H10.0909ZM13.25 11.5H10.5V8.00003H13.25C14.2165 8.00003 15 8.78353 15 9.75003C15 10.7165 14.2165 11.5 13.25 11.5Z"/>
                </svg>
              </div>
            `
              : '') +
            (event.extendedProps.appointment.pending_financial === 0
            ?
              `<div class="scheduling-status-icon financial">
                <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.70414 11.8399C9.92672 11.8399 12.5391 9.2275 12.5391 6.00492C12.5391 2.78234 9.92672 0.169922 6.70414 0.169922C3.48156 0.169922 0.869141 2.78234 0.869141 6.00492C0.869141 9.2275 3.48156 11.8399 6.70414 11.8399ZM6.7045 1.81104C7.04765 1.81104 7.32582 2.08921 7.32582 2.43235V3.00131C7.63832 2.99887 7.94642 3.07728 8.22006 3.22931C8.50736 3.38891 8.74584 3.62364 8.90998 3.90837C9.08135 4.20566 8.97929 4.58558 8.682 4.75696C8.38472 4.92834 8.00479 4.82627 7.83341 4.52899C7.78183 4.4395 7.70688 4.36573 7.61658 4.31557C7.52629 4.2654 7.42406 4.24074 7.32083 4.24422C7.31386 4.24445 7.30689 4.24457 7.29992 4.24457H6.10906C5.958 4.24457 5.81314 4.30457 5.70633 4.41138C5.59952 4.51819 5.53951 4.66306 5.53951 4.81411C5.53951 4.96516 5.59952 5.11003 5.70633 5.21684C5.81314 5.32365 5.958 5.38365 6.10906 5.38365H7.29992C7.78054 5.38365 8.24148 5.57458 8.58132 5.91443C8.92117 6.25428 9.1121 6.71522 9.1121 7.19584C9.1121 7.67646 8.92117 8.13739 8.58132 8.47724C8.24759 8.81098 7.79708 9.0011 7.32582 9.00783V9.57753C7.32582 9.92067 7.04765 10.1988 6.7045 10.1988C6.36136 10.1988 6.08318 9.92067 6.08318 9.57753V9.00864C5.77068 9.01109 5.46256 8.93267 5.18891 8.78064C4.90162 8.62103 4.66314 8.3863 4.499 8.10158C4.32762 7.80429 4.42969 7.42436 4.72697 7.25299C5.02426 7.08161 5.40418 7.18367 5.57556 7.48096C5.62715 7.57045 5.7021 7.64422 5.79239 7.69438C5.88268 7.74454 5.98492 7.76921 6.08815 7.76573C6.09512 7.7655 6.10209 7.76538 6.10906 7.76538H6.69809L6.7045 7.76535L6.71092 7.76538H7.29992C7.45097 7.76538 7.59584 7.70537 7.70265 7.59856C7.80946 7.49175 7.86946 7.34689 7.86946 7.19584C7.86946 7.04478 7.80946 6.89992 7.70265 6.79311C7.59584 6.6863 7.45097 6.62629 7.29992 6.62629H6.10906C5.62844 6.62629 5.1675 6.43537 4.82765 6.09552C4.4878 5.75567 4.29688 5.29473 4.29688 4.81411C4.29688 4.33349 4.4878 3.87256 4.82765 3.53271C5.1614 3.19896 5.61192 3.00883 6.08318 3.00211V2.43235C6.08318 2.08921 6.36136 1.81104 6.7045 1.81104Z" fill="#0B30B2"/>
                </svg>
                </div>`
                : '') +
            (this.hasProcedure(
              event.extendedProps.appointment,
              'EXAM'
            )
              ? `
              <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()} exam">
                <svg viewBox="0 0 20 20" fill="black" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM7.37812 16C6.61701 16 6 15.3722 6 14.5979V5.40214C6 4.62776 6.61701 4 7.37812 4H12.9503C13.53 4 14 4.47814 14 5.06797C14 5.65779 13.53 6.13593 12.9503 6.13593H9.35573C8.84832 6.13593 8.43698 6.55444 8.43698 7.07069V7.9073C8.43698 8.42356 8.84832 8.84207 9.35573 8.84207H12.5966C13.1763 8.84207 13.6463 9.32021 13.6463 9.91003C13.6463 10.4999 13.1763 10.978 12.5966 10.978H9.35573C8.84832 10.978 8.43698 11.3965 8.43698 11.9128V12.9293C8.43698 13.4456 8.84832 13.8641 9.35573 13.8641H12.9503C13.53 13.8641 14 14.3422 14 14.932C14 15.5219 13.53 16 12.9503 16H7.37812ZM6.22969 14.5979C6.22969 15.2432 6.74386 15.7663 7.37812 15.7663H12.9503C13.032 15.7663 13.1108 15.7542 13.1852 15.7316C13.1108 15.7542 13.032 15.7663 12.9504 15.7663H7.37817C6.74391 15.7663 6.22974 15.2432 6.22974 14.5978V5.40212C6.22974 4.91688 6.52045 4.50073 6.93435 4.32412C6.52043 4.50072 6.22969 4.91688 6.22969 5.40214V14.5979ZM9.35573 10.7443C9.23402 10.7443 9.11674 10.7636 9.00666 10.7993C9.11676 10.7636 9.23405 10.7443 9.35578 10.7443H12.5967C12.6745 10.7443 12.7498 10.7332 12.8212 10.7126C12.7498 10.7333 12.6745 10.7443 12.5966 10.7443H9.35573ZM9.35573 5.90224C9.23588 5.90224 9.12032 5.92092 9.01171 5.95557C9.12034 5.92091 9.23591 5.90222 9.35578 5.90222H12.9504C13.026 5.90222 13.0993 5.8918 13.1688 5.87229C13.0993 5.89181 13.026 5.90224 12.9503 5.90224H9.35573Z"/>
                </svg>
              </div>
            `
              : '') +
            (this.hasProcedure(
              event.extendedProps.appointment,
              'TELEMEDICINE'
            )
              ? `
              <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                <svg viewBox="0 0 24 24" fill="#0C1D59" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23 7L16 12L23 17V7Z"/>
                  <path d="M14 5H3C1.89543 5 1 5.89543 1 7V17C1 18.1046 1.89543 19 3 19H14C15.1046 19 16 18.1046 16 17V7C16 5.89543 15.1046 5 14 5Z"/>
                </svg>
              </div>
            `
              : '') +
            (this.hasProcedure(
              event.extendedProps.appointment,
              'APPOINTMENT'
            )
              ? `
              <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.25 12C8.25 9.92893 9.92893 8.25 12 8.25H13.25C13.9404 8.25 14.5 7.69036 14.5 7C14.5 6.30964 13.9404 5.75 13.25 5.75H12C8.54822 5.75 5.75 8.54822 5.75 12C5.75 15.4518 8.54822 18.25 12 18.25H13.25C13.9404 18.25 14.5 17.6904 14.5 17C14.5 16.3096 13.9404 15.75 13.25 15.75H12C9.92893 15.75 8.25 14.0711 8.25 12Z"/>
                </svg>
              </div>
            `
              : '') +
            (this.hasProcedure(
              event.extendedProps.appointment,
              'RETURN'
            )
              ? `
              <div class="fc-icon icon-${status}">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM9.59091 6C8.71263 6 8 6.71176 8 7.59075V17C8 17.5523 8.44772 18 9 18C9.55228 18 10 17.5517 10 16.9994V13.5H12.75C14.8211 13.5 16.5 11.8211 16.5 9.75C16.5 7.67893 14.8211 6 12.75 6H9.59091ZM12.75 11.5H10V8H12.75C13.7165 8 14.5 8.7835 14.5 9.75C14.5 10.7165 13.7165 11.5 12.75 11.5Z"/>
                    <path d="M11.5 12.5H12.5585C13.4193 12.5 14.1836 13.0509 14.4558 13.8675L15.5 17" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            `
              : '') +
  
              (this.hasProcedure(
                event.extendedProps.appointment,
                'SURGICAL'
              )
              ? `
              <div class="fc-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M5.55185 10.9093C5.55185 13.9217 7.99393 16.3638 11.0064 16.3638C14.0189 16.3638 16.4609 13.9217 16.4609 10.9093C16.4609 7.91784 14.0528 5.48886 11.0694 5.45508C13.1921 5.48206 14.9024 6.86702 14.9024 8.57156C14.9024 10.293 13.158 11.6884 11.0063 11.6884C8.85452 11.6884 7.11018 10.293 7.11018 8.57156C7.11018 6.8675 8.81954 5.48284 10.9414 5.4551C7.95886 5.48995 5.55185 7.91851 5.55185 10.9093Z" />
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M8.45597 9.09144C8.45597 9.60178 9.15608 10.7278 11.0014 10.7278C12.8468 10.7278 13.5469 9.60178 13.5469 9.09144C13.5469 8.5811 12.8468 7.45508 11.0014 7.45508C9.15608 7.45508 8.45597 8.5811 8.45597 9.09144ZM6.45597 9.09144C6.45597 11.0997 8.49104 12.7278 11.0014 12.7278C13.5118 12.7278 15.5469 11.0998 15.5469 9.09144C15.5469 7.08313 13.5118 5.45508 11.0014 5.45508C8.49104 5.45508 6.45597 7.08313 6.45597 9.09144Z" />
                  <path d="M16.7244 2.61743C17.0794 2.26241 17.0794 1.68681 16.7244 1.33178C16.3693 0.976763 15.7937 0.976763 15.4387 1.33178L14.1531 2.61743C13.798 2.97245 13.798 3.54806 14.1531 3.90308C14.5081 4.2581 15.0837 4.2581 15.4387 3.90308L16.7244 2.61743Z" />
                  <path d="M11.9141 0.909091C11.9141 0.407013 11.507 0 11.005 0C10.5029 0 10.0959 0.407014 10.0959 0.909091V8.18182C10.0959 8.6839 10.5029 9.09091 11.005 9.09091C11.507 9.09091 11.9141 8.6839 11.9141 8.18182V0.909091Z" />
                  <path d="M6.91548 1.27558C6.66444 0.840766 6.10845 0.691788 5.67364 0.942827C5.23883 1.19387 5.08985 1.74986 5.34089 2.18467L6.24998 3.75926C6.50102 4.19407 7.05701 4.34305 7.49182 4.09201C7.92663 3.84097 8.07561 3.28498 7.82457 2.85017L6.91548 1.27558Z" />
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M2.14219 12.7278C2.23741 12.8909 2.35946 13.0922 2.50765 13.3216C2.92464 13.9673 3.54203 14.8257 4.34317 15.6802C5.9637 17.4088 8.21769 19.0005 11 19.0005C13.7823 19.0005 16.0363 17.4088 17.6568 15.6802C18.458 14.8257 19.0754 13.9673 19.4923 13.3216C19.6405 13.0922 19.7626 12.8909 19.8578 12.7278C19.7626 12.5647 19.6405 12.3634 19.4923 12.134C19.0754 11.4883 18.458 10.6299 17.6568 9.77538C16.0363 8.04682 13.7823 6.45508 11 6.45508C8.21769 6.45508 5.9637 8.04682 4.34317 9.77538C3.54203 10.6299 2.92464 11.4883 2.50765 12.134C2.35946 12.3634 2.23741 12.5647 2.14219 12.7278ZM21 12.7278C21.8944 12.2806 21.8943 12.2803 21.8941 12.2799L21.8925 12.2768L21.8893 12.2704L21.8787 12.2497C21.8699 12.2325 21.8575 12.2086 21.8415 12.1784C21.8095 12.118 21.7633 12.0323 21.7029 11.925C21.5823 11.7106 21.4049 11.4089 21.1724 11.0489C20.7087 10.3309 20.0193 9.37113 19.1159 8.4075C17.3273 6.4997 14.5813 4.45508 11 4.45508C7.41867 4.45508 4.67266 6.4997 2.8841 8.4075C1.9807 9.37113 1.29127 10.3309 0.827574 11.0489C0.595106 11.4089 0.417738 11.7106 0.297103 11.925C0.236749 12.0323 0.190482 12.118 0.158514 12.1784C0.142527 12.2086 0.130105 12.2325 0.121275 12.2497L0.110724 12.2704L0.107472 12.2768L0.106354 12.279C0.106173 12.2794 0.105573 12.2806 1 12.7278L0.105573 12.2806C-0.0351909 12.5621 -0.0351909 12.8935 0.105573 13.175L1 12.7278C0.105573 13.175 0.105392 13.1747 0.105573 13.175L0.106354 13.1766L0.107472 13.1788L0.110724 13.1852L0.121275 13.2059C0.130105 13.2231 0.142527 13.247 0.158514 13.2772C0.190482 13.3376 0.236749 13.4233 0.297103 13.5306C0.417738 13.745 0.595106 14.0467 0.827574 14.4067C1.29127 15.1247 1.9807 16.0845 2.8841 17.0481C4.67266 18.9559 7.41867 21.0005 11 21.0005C14.5813 21.0005 17.3273 18.9559 19.1159 17.0481C20.0193 16.0845 20.7087 15.1247 21.1724 14.4067C21.4049 14.0467 21.5823 13.745 21.7029 13.5306C21.7633 13.4233 21.8095 13.3376 21.8415 13.2772C21.8575 13.247 21.8699 13.2231 21.8787 13.2059L21.8893 13.1852L21.8925 13.1788L21.8936 13.1766C21.8938 13.1762 21.8944 13.175 21 12.7278ZM21 12.7278L21.8944 13.175C22.0352 12.8935 22.0348 12.5614 21.8941 12.2799L21 12.7278Z" />
                </svg>
              </div>
            `
              : '') +
            '</div>' +
  
          '<div class="attendance-status">' +
          (event.extendedProps.appointment.status === 'EXAMINATING'
          ?
            `<div class="scheduling-status-icon examinating-icon">
              <svg viewBox="0 0 24 24" fill="#0C1D59" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M2 13H8.06189C8.55399 16.9463 11.9204 20 16 20C20.4183 20 24 16.4183 24 12C24 7.58172 20.4183 4 16 4C11.9204 4 8.55399 7.05369 8.06189 11H2C2 10.4477 1.55228 10 1 10C0.447715 10 0 10.4477 0 11V12V13C0 13.5523 0.447715 14 1 14C1.55228 14 2 13.5523 2 13ZM11.1696 15.4502C10.6471 14.7448 10.2613 13.9024 10.0932 13H11C11.5523 13 12 12.5523 12 12C12 11.4477 11.5523 11 11 11H10.0932C10.2614 10.0976 10.6471 9.25526 11.1696 8.54984C11.8971 7.5678 12.7813 6.99122 13.4851 6.81529C14.0884 6.66446 14.5237 6.78701 14.9099 7.24405C15.4071 7.83238 16 9.2091 16 12C16 14.791 15.4071 16.1677 14.9099 16.756C14.5237 17.2131 14.0884 17.3356 13.4851 17.1848C12.7813 17.0088 11.8971 16.4323 11.1696 15.4502ZM16.4928 6.01994C17.3995 7.13959 18 9.05937 18 12C18 14.9406 17.3995 16.8604 16.4928 17.98C19.576 17.7294 22 15.1478 22 12C22 8.85221 19.576 6.27057 16.4928 6.01994Z"/>
              </svg>
            </div>`
              : '') +
          (event.extendedProps.appointment.status === 'DILATING'
          ?
            `<div class="scheduling-status-icon dilating-icon">
              <svg viewBox="0 0 24 24" fill="#0C1D59" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M12 4C5 4 1 12 1 12C1 12 5 20 12 20C19 20 23 12 23 12C23 12 19 4 12 4ZM10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12ZM12 8.00001C9.79086 8.00001 8 9.79087 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79087 14.2091 8.00001 12 8.00001Z"/>
              </svg>
            </div>`
              : '') +
          (event.extendedProps.appointment.type === 'Reagendamento'
          ?
            `<div class="scheduling-status-icon scheduling_${event.extendedProps.appointment.status.toLowerCase()}_icon">
              <svg width="12" height="12" viewBox="0 0 13 16" fill="#525C7A" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.79336 1.63232C9.79336 1.21811 9.45757 0.882324 9.04336 0.882324C8.62914 0.882324 8.29336 1.21811 8.29336 1.63232V2.15573H4.69378V1.63232C4.69378 1.21811 4.35799 0.882324 3.94378 0.882324C3.52957 0.882324 3.19378 1.21811 3.19378 1.63232V2.15573H2.02596C0.908376 2.15573 0 3.06039 0 4.1793V6.72618V13.0943C0 14.2132 0.908376 15.1179 2.02596 15.1179H10.9577C12.0752 15.1179 12.9836 14.2132 12.9836 13.0943V6.72618V4.1793C12.9836 3.06039 12.0752 2.15573 10.9577 2.15573H9.79336V1.63232ZM11.4836 5.97618V4.1793C11.4836 3.89146 11.2495 3.65573 10.9577 3.65573H9.79336V4.17947C9.79336 4.59368 9.45757 4.92947 9.04336 4.92947C8.62914 4.92947 8.29336 4.59368 8.29336 4.17947V3.65573H4.69378V4.17947C4.69378 4.59368 4.35799 4.92947 3.94378 4.92947C3.52957 4.92947 3.19378 4.59368 3.19378 4.17947V3.65573H2.02596C1.73415 3.65573 1.5 3.89146 1.5 4.1793V5.97618H11.4836ZM1.5 7.47618H11.4836V13.0943C11.4836 13.3821 11.2495 13.6179 10.9577 13.6179H2.02596C1.73415 13.6179 1.5 13.3821 1.5 13.0943V7.47618Z"/>
              <path d="M10.7499 2.54199H2.24352C1.57238 2.54199 1.02832 2.75921 1.02832 3.02716V6.42335C1.02832 6.6913 1.57238 6.90852 2.24352 6.90852H10.7499C11.421 6.90852 11.9651 6.6913 11.9651 6.42335V3.02716C11.9651 2.75921 11.421 2.54199 10.7499 2.54199Z"/>
              </svg>
            </div>`
              : '') +
          (event.extendedProps.appointment.type === 'Encaixe'
          ?
            `<div class="realocate-appointment-icon icon-${event.extendedProps.appointment.status.toLowerCase()}">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6ZM6 2.15789C3.87806 2.15789 2.15789 3.87806 2.15789 6C2.15789 8.12194 3.87806 9.8421 6 9.8421C8.12194 9.8421 9.8421 8.12194 9.8421 6C9.8421 3.87806 8.12194 2.15789 6 2.15789ZM1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6ZM6.26664 3.23584C6.26664 2.9216 6.0204 2.66687 5.71664 2.66687C5.41288 2.66687 5.16664 2.9216 5.16664 3.23584V5.64963C5.16664 5.80053 5.22459 5.94525 5.32773 6.05195L6.72773 7.50022C6.94252 7.72242 7.29076 7.72242 7.50555 7.50022C7.72034 7.27803 7.72034 6.91778 7.50555 6.69559L6.26664 5.41396V3.23584Z"/>
              </svg>
            </div>`
              : '') +
             (this.currentView === 'timeGridDay' ?
              `<div class="ml-2 fc-time time-${event.extendedProps.appointment.status.toLowerCase()} fc-hours">` +
                this.moment(event.start).format('HH:mm') +
                ' até ' +
                this.moment(event.end).format('HH:mm') +
              '</div>' : '') +
            '</div>' +
            '</div>' +
            '</div>' +
            '</div>'
        }
        return { html: eventContent }
      },
      handleMoreLinkContent(arg) {
        return 'Mais ' + arg.num
      },
      hasProcedure(data, procedureType) {
        return data.appointment_items.some(p => p.item?.type === procedureType)
      },
      translateDays(day){
        const weekDays = {
          'DOMINGO': 0,
          'SEGUNDA-FEIRA': 1,
          'TERCA-FEIRA': 2,
          'QUARTA-FEIRA': 3,
          'QUINTA-FEIRA': 4,
          'SEXTA-FEIRA': 5,
          'SABADO': 6
        }
        return weekDays[day]
      },
      checkPeriods(){
        if(this.currentView === 'timeGridDay'){
          this.start = this.moment().format('YYYY-MM-DD')
        }
        this.api
          .checkPeriods(this.clinic.id)
          .then(res => { this.periodsForm = res.data });
      },
      createPatient(patient) {
        this.patient = patient
      },
      setAppointmentId(id){
        this.appointmentId = id
      },
      async reloadEvents() {
        this.calendarOptions.events = this.events
        this.allEvents = this.calendarOptions.events
        let firstAppointmentTime = this.allEvents?.length && this.moment(this.allEvents[0].start).isValid() ? 
            this.moment(this.allEvents[0].start).format('HH:mm:ss') : null
        let lastAppointmentTime = this.allEvents?.length && this.moment(this.allEvents[this.allEvents?.length - 1].end).isValid() ?
            this.moment(this.allEvents[this.allEvents?.length - 1].end).format('HH:mm:ss') : null
        
        const api = this.$refs.fullCalendar?.getApi()
        if (!api) return
        const minTime = api.currentDataManager.stableDynamicOptionOverrides.slotMinTime
        const maxTime = api.currentDataManager.stableDynamicOptionOverrides.slotMaxTime

        firstAppointmentTime = firstAppointmentTime < minTime ? firstAppointmentTime : minTime
        lastAppointmentTime = lastAppointmentTime > maxTime ? lastAppointmentTime : maxTime
        
        await this.updateOpeningHours(firstAppointmentTime, lastAppointmentTime, this.openingMinutes);
      }
    },
    watch: {
      selectedSchedules() {
        this.professionalId = null
        this.roomId = null

        if (this.selectedSchedules?.length === 1 && this.selectedSchedules[0].type === 'doctor') {
          this.professionalId = this.selectedSchedules[0].id
        }

        if (this.selectedSchedules?.length === 1 && this.selectedSchedules[0].type === 'room') {
          this.roomId = this.selectedSchedules[0].id
        }

        this.filterChanged();
      },
      async events() {    
        await this.reloadEvents()
      }
    }
  }
  </script>
  <style lang="scss" src="@/modules/schedule/styles/calendar.scss"></style>
  
  <style lang="scss">
  
  .popover, .popover-body{
    max-width: 500px !important;
    z-index: 9999 !important;
  }
  .calendar-title {
    border-top: 1px solid var(--neutral-200);
    padding-top: 15px;
    margin-bottom: -20px;
    display: flex;
    justify-content: center !important;
  
    .day-title {
      font-weight: 600;
      font-size: 16px;
      color: var(--type-active);
    }
  }
  .tooltip-patient {
    min-width: 270px !important;
    max-width: 400px !important;
    padding-right: 5px;
  
    .fc-icon {
      margin-right: 5px;
      svg {
        width: 14px !important;
        height: 14px !important;
        fill: var(--blue-500);
      }
  
      .appointment {
        margin-top: 2px;
      }
  
      .surgical path {
        fill: var(--blue-500) !important;
      }
    }
  
    .d-flex {
      margin-left: 5px;
      svg {
        width: 24px;
        height: 24px;
      }
  
      .svg-fill {
        svg {
          fill: var(--blue-500);
        }
      }
    }
  
    .tooltip-text {
      font-family: 'Nunito Sans';
      font-style: normal;
      line-height: 150%;
      font-size: 16px;
      font-weight: 600;
      color: var(--type-active);
      display: inline-block;
      vertical-align: top;
      max-width: 370px;
      &.patient-name {
        font-weight: 700;
        max-width: 135px;
      }
  
      &.info {
        color: var(--neutral-600);
      }
  
      &.description {
        max-width: 370px;
        margin-bottom: 9px !important;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
  
      &.whatsapp-link {
        svg {
          margin-top: -2px;
        }
      }
  
      &.surgical-blue {
        svg path {
          fill: var(--blue-500) !important;
        }
      }
    }
  
    .border-bt {
      color: var(--type-active);
      display: inline-block;
      width: 400px;
      border-bottom: 1px solid #dee2e6;
  
      .surgical {
        :host::ng-deep path {
          fill: var(--blue-500) !important;
        }
      }
    }
  }
  
  .surgical-blue path {
    fill: var(--blue-500) !important;
  }
  
  </style>
  